import { ApolloError, gql } from "@apollo/client"
import {
  FilterSelection,
  OrderBy,
  SearchShipmentLegsRequest,
  ShipmentLegFragment,
  useShipmentLegsQuery,
} from "gen/graphql"
import { produce } from "immer"
import { DateTime } from "luxon"

const NUMBER_OF_SHIPMENT_LEGS_PER_PAGE = 20
export function useShipmentLegs(
  metricId: string,
  filterSelection: FilterSelection[],
  fromDate: DateTime,
  toDate: DateTime,
  orderBy: OrderBy[],
): {
  data: ShipmentLegFragment[]
  loading: boolean
  error: ApolloError | undefined
  loadMore: () => void
  hasNextPage: boolean
} {
  const requestArgs: SearchShipmentLegsRequest = {
    filters: filterSelection.map((filter) => ({
      filterAttributeId: filter.filterAttributeId,
      filterTermIds: filter.filterTermIds,
    })),
    dateSpan: {
      startDate: {
        year: fromDate.year,
        month: fromDate.month,
        day: fromDate.day,
      },
      endDate: {
        year: toDate.year,
        month: toDate.month,
        day: toDate.day,
      },
    },
    orderBy,
    pagination: { first: NUMBER_OF_SHIPMENT_LEGS_PER_PAGE },
  }
  const { data, loading, error, fetchMore } = useShipmentLegsQuery({
    variables: {
      request: requestArgs,
    },
    skip: !metricId,
  })

  function loadMore(): void {
    const nextCursor = data ? data.SearchShipmentLegs.pagination.endCursor : undefined

    if (nextCursor && !loading) {
      fetchMore({
        variables: {
          request: produce(requestArgs, (draft) => {
            // eslint-disable-next-line no-param-reassign
            draft.pagination.after = nextCursor
          }),
        },
        updateQuery(previousQueryResult, { fetchMoreResult }) {
          return {
            SearchShipmentLegs: {
              shipmentLegs: [
                ...previousQueryResult.SearchShipmentLegs.shipmentLegs,
                ...fetchMoreResult.SearchShipmentLegs.shipmentLegs,
              ],
              pagination: fetchMoreResult.SearchShipmentLegs.pagination,
            },
          }
        },
      })
    }
  }

  const shipmentLegs = data?.SearchShipmentLegs.shipmentLegs ?? []
  const hasNextPage = data ? data.SearchShipmentLegs.pagination.hasNextPage : false

  return { data: shipmentLegs, loading, error, hasNextPage, loadMore }
}

export const DATE_FRAGMENT = gql`
  fragment DateTime on DateTime {
    year
    month
    day
    hours
    minutes
    seconds
  }
`

export const TIMESTAMP_OR_DATE = gql`
  fragment TimestampOrDate on TimestampOrDate {
    timestamp
    date {
      year
      month
      day
    }
  }
`

export const SHIPMENT_LEG_FRAGMENT = gql`
  fragment ShipmentLeg on ShipmentLeg {
    deliveredByVehicleDisplayName
    deliveryReportedCompleteDate {
      ...Date
    }
    deliveryReportedCompleteTime
    deliveryReportedCompleteTimeLocalTimezone {
      ...DateTime
    }
    deliveryReportedCompleteAt {
      ...TimestampOrDate
    }
    destinationCity
    destinationCountryCode
    destinationLocationDisplayName
    destinationLocationId
    destinationPostalCode
    laneDisplayName
    laneId
    name
    originCity
    originCountryCode
    originLocationDisplayName
    originLocationId
    originPostalCode
    ownerOrganizationName
    shipment
    space
    spaceDisplayName
    status
    vehicleDriveType
    vehicleEnergySource
    shipmentExternalReferenceId
    shipmentReleaseTime
  }
`

export const SHIPMENT_LEGS_QUERY = gql`
  query shipmentLegs($request: SearchShipmentLegsRequest!) {
    SearchShipmentLegs(request: $request) {
      shipmentLegs {
        ...ShipmentLeg
      }
      pagination {
        endCursor
        hasNextPage
      }
    }
  }
`
